import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Inject, Input, Output,
  PLATFORM_ID, TemplateRef, ViewChild, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy
} from '@angular/core';

import { DOCUMENT, isPlatformServer, NgTemplateOutlet } from '@angular/common';
import { fromEvent, Subject, Subscription, map, pairwise } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { ITabBrand } from '../../_interfaces/header.interface';
import { BrandsEnum } from '../../_enums/brands.enum';
import { DeviceService } from '../../_services/device.service';
import { fadeInOutAnimation } from '../../_animations/fade-in-out.animation';
import { GetElementUtils } from '../../utils/getElement.utils';
import { IconSizeEnum } from '../../_enums/icon.enum';

import {
  IDropdownAccountData, IHeaderIconLink, IIconLabel, ILanguageSelectorData, IMenuDataConfig, ISearchData
} from '../../_interfaces/header.interface';

import { ILink } from '../../_interfaces/link.interface';
import { IPushCard, IPushCardWithLayout } from '../../_interfaces/push-card.interface';
import { PositionEnum } from '../../_enums/position.enum';
import { SdLinkComponent } from '../links/link/link.component';
import { SdDiscoverBannerComponent } from '@seb/web-design-system/discover-banner';
import { SdMenuComponent } from '../menu/menu.component';
import { SdButtonComponent } from '../buttons/button/button.component';
import { SdLanguageSelectorComponent } from '../language-selector/language-selector.component';
import { SdSearchComponent } from '../search/search.component';
import { IDropdownCart } from '../navigation-dropdown/dropdown-cart/dropdown-cart.interface';
import { SdDropdownCartComponent } from '../navigation-dropdown/dropdown-cart/dropdown-cart.component';
import { SdDropdownAccountComponent } from '../navigation-dropdown/dropdown-account/dropdown-account.component';
import { SdDropdownHelpComponent } from '../navigation-dropdown/dropdown-help/dropdown-help.component';
import { SdPushCardComponent } from '../push-card/push-card.component';
import { Direction } from '../../_enums/direction.enum';
import { IPromo } from '../product-promo/product-promo.interface';
import { SdBrandBarComponent } from '../brand-bar/brand-bar.component';
import { SdPageNavigationComponent } from '../page-navigation/page-navigation.component';
import { IPageNavigation } from '../page-navigation/page-navigation.interface';
import { SdHeaderReinsurancesComponent } from '../header-reinsurances/header-reinsurances.component';

@Component({
  selector: 'sd-header-with-tabs',
  templateUrl: './header-with-tabs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    fadeInOutAnimation({
      anchor: 'fadeInOut',
      durationIn: 0,
      delayIn: 0,
      durationOut: 0,
      delayOut: 0
    })
  ],
  standalone: true,
  imports: [
    NgTemplateOutlet,
    SdLinkComponent,
    SdDiscoverBannerComponent,
    SdButtonComponent,
    SdLanguageSelectorComponent,
    SdSearchComponent,
    SdDropdownHelpComponent,
    SdDropdownAccountComponent,
    SdDropdownCartComponent,
    TranslateModule,
    SdMenuComponent,
    SdBrandBarComponent,
    SdPageNavigationComponent,
    SdHeaderReinsurancesComponent
  ]
})
export class SdHeaderWithTabsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  pushTemplate: TemplateRef<SdPushCardComponent>;

  @Input()
  deliveryContactData: IIconLabel[];

  @Input()
  discoverBrandsText: string;

  @Input()
  isSimplified = false;

  @Input()
  logoLink: ILink;

  @Input()
  languageSelectorData: ILanguageSelectorData;

  @Input()
  searchData: ISearchData;

  @Input()
  inputMinLength: number;

  @Input()
  iconLinks: IHeaderIconLink[];

  @Input()
  dropdownAccountData: IDropdownAccountData;

  @Input()
  dropdownCartData: IDropdownCart;

  @Input()
  dropdownHelpData: ILink[];

  @Input()
  tabsBrands: ITabBrand[];

  @Input()
  menuDataConfig: IMenuDataConfig;

  @Input()
  pageNavigationData: IPageNavigation;

  @Input()
  isBundle: boolean;

  @Input()
  showPageNavigation: boolean;

  @Input({ transform: (b: string) => b?.toUpperCase() })
  activeBrand: BrandsEnum;

  @Output()
  back = new EventEmitter<void>();

  @Output()
  languageSelected = new EventEmitter<string>();

  @Output()
  inputSearch = new EventEmitter<string>();

  @Output()
  submitSearch = new EventEmitter<string>();

  @Output()
  clearInputSearch = new EventEmitter<void>();

  @Output()
  searchElementClicked = new EventEmitter<string>();

  @Output()
  menuOpen = new EventEmitter<boolean>();

  @Output()
  scrolled = new EventEmitter<boolean>();

  @Output()
  openRecommendedPriceModal = new EventEmitter<void>();

  @Output()
  login = new EventEmitter<void>();

  @Output()
  logout = new EventEmitter<void>();

  @Output()
  signUp = new EventEmitter<void>();

  @Output()
  delete = new EventEmitter<string>();

  @Output()
  isAccountOpenedChange = new EventEmitter<boolean>();

  @Output()
  empty = new EventEmitter<void>();

  @Output()
  quantity = new EventEmitter<{ product: string; qty: number }>();

  @Output()
  promoClick = new EventEmitter<IPromo>();

  @Output()
  isCartOpenedChange = new EventEmitter<boolean>();

  @Output()
  tabSelected = new EventEmitter<BrandsEnum>();

  @Output()
  clickMenu = new EventEmitter<string>();

  @Output()
  linkClicked = new EventEmitter<any>();

  @Output()
  pushCardClicked = new EventEmitter<IPushCard | IPushCardWithLayout>();

  @Output()
  clickCardFrames = new EventEmitter<IPushCard | IPushCardWithLayout>();

  @Output()
  cart = new EventEmitter<void>();

  @Output()
  notify = new EventEmitter<void>();

  @Output()
  favorite = new EventEmitter<void>();

  @ViewChild('menuComponent')
  public menuComponent: SdMenuComponent;

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.menuHidden ? 'is-scrolling-down' : '',
      this.isScrolled ? 'is-scrolled' : '',
      this.showOverlay ? 'is-open' : '',
      this.isSimplified ? 'is-simplified' : '',
      this.showPageNavigation ? 'has-navigation' : '',
      this.activeBrand ? 'is-theme-' + this.activeBrand.toLowerCase() : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  subscriptions = new Subscription();

  isDevice$: boolean;
  isMobile$: boolean;
  isSSR!: boolean;
  showOverlay: boolean;
  menuHidden = false;
  isScrolled = false;

  IconSizeEnum = IconSizeEnum;
  PositionEnum = PositionEnum;
  BrandsEnum = BrandsEnum;

  getElementUtils = GetElementUtils;
  openMobileSearch: Subject<void> = new Subject<void>();
  urlIcons = '/assets/icons/';

  constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly deviceService: DeviceService,
    private readonly elRef: ElementRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) readonly platformId: any
  ) {
    this.isSSR = isPlatformServer(platformId);
  }

  /**
   * Close Menu if focus is outside the header component
   */
  @HostListener('focusout', ['$event'])
  onFocusOut(event) {
    const target = event.target || event.currentTarget;
    if (!this.isSimplified && !this.elRef.nativeElement.contains(target)) {
      this.menuComponent?.closeMenu();
    }
  }

  /**
   * Close Menu if focus is outside the header component
   */
  @HostListener('document:keydown.tab', ['$event'])
  onTabsOut(event) {
    const target = event.target || event.currentTarget;
    if (!this.isSimplified && !this.elRef.nativeElement.contains(target)) {
      this.menuComponent?.closeMenu();
    }
  }

  ngOnInit() {
    this.subscriptions.add(
      this.deviceService.isDevice.subscribe((x) => {
        this.isDevice$ = x;
        this.cdRef.markForCheck();
      })
    );
    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.cdRef.markForCheck();
      })
    );
  }

  ngAfterViewInit() {
    if (!this.isSSR) {
      this.subscriptions.add(
        fromEvent(window, 'scroll').pipe(
          map(() => window.pageYOffset),
          pairwise(),
          map(([y1, y2]): [number, Direction] => [y2, y2 < y1 ? Direction.up : Direction.down])
        ).subscribe(([yOffset, direction]) => {
          if (
            direction === Direction.up ||
            yOffset <= 55 ||
            (direction === Direction.down && yOffset <= 0)
          ) {
            this.menuHidden = false;
            this.isScrolled = false;
            this.menuOpen.emit(!this.menuHidden);
            this.scrolled.emit(!this.isScrolled);
          }

          if (direction === Direction.down && yOffset > 0) {
            this.menuHidden = true;
            this.menuOpen.emit(!this.menuHidden);
          }

          if (direction === Direction.up && yOffset > 0) {
            this.isScrolled = true;
            this.scrolled.emit(!this.isScrolled);
          }

          const body = this.getElementUtils.byId('appWrapper', this.document.body);

          if (body.classList.contains('has-no-overflow')) {
            this.menuHidden = false;
            this.menuOpen.emit(!this.menuHidden);
          }

          this.cdRef.markForCheck();
        })
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  changeLanguage(value: string) {
    this.languageSelected.emit(value);
  }

  onMenuOpenedChanged(event: boolean) {
    if (!this.isSSR) {
      this.showOverlay = event;
    }
  }

  onClickOverlay() {
    this.menuComponent?.closeMenu();
  }

  goBack() {
    this.back.emit();
  }

  onSearch(event: string) {
    this.inputSearch.emit(event);
  }

  onSubmitSearch(event: string) {
    this.submitSearch.emit(event);
  }

  onClearInputSearch() {
    this.clearInputSearch.emit();
  }

  onOpenedSearch(event?: string) {
    if (event) {
      this.searchElementClicked.emit(event);
    }
    this.menuComponent?.closeMenu();
  }

  onopenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }

  toggleSearch() {
    this.openMobileSearch.next();
  }

  onSignUp() {
    this.signUp.emit();
  }

  onLogin() {
    this.login.emit();
  }

  onLogout() {
    this.logout.emit();
  }

  onDelete(product: string) {
    this.delete.emit(product);
  }

  onEmptyCart() {
    this.empty.emit();
  }

  onQuantityChange(event) {
    this.quantity.emit({ product: event.product, qty: event.qty });
  }

  onClickPromo(promo: IPromo) {
    this.promoClick.emit(promo);
  }

  selectBrand(brand: BrandsEnum) {
    this.tabSelected.emit(brand);
  }

  onLinkClicked(event) {
    this.linkClicked.emit(event);
  }

  onPushCardClicked(event: IPushCard | IPushCardWithLayout) {
    this.pushCardClicked.emit(event);
  }

  onClickCardFrames(event: IPushCard | IPushCardWithLayout) {
    this.clickCardFrames.emit(event);
  }

  onClickMenu(event) {
    this.clickMenu.emit(event);
  }

  onAddToCart() {
    this.cart.emit();
  }

  onNotify() {
    this.notify.emit();
  }

  onAddToFavorite() {
    this.favorite.emit();
  }
}
